import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CalendarCell } from './calendar-cell';

/**
 * Reusable calendar grid renderer component. Displays a table of calendar cells (dates, months, or years). Used by
 * MonthView, YearView, and MultiYearView components.
 *
 * This component follows Angular Material's CalendarBody pattern, providing a generic grid renderer that can display
 * any type of calendar data (days, months, years).
 *
 * @example
 *   ```html
 *   <hx-calendar-body
 *     [label]="'January 2025'"
 *     [rows]="dateRows"
 *     [numCols]="7"
 *     [activeCell]="15"
 *     [selectedCell]="10"
 *     [cellAspectRatio]="1"
 *     (selectedChange)="onDateSelected($event)"
 *   />
 *   ```;
 */
@Component({
  selector: 'hx-calendar-body',
  standalone: true,
  imports: [NgClass],
  templateUrl: './calendar-body.html',
  styleUrls: ['./calendar-body.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'hx-calendar-body',
    '[attr.aria-label]': 'label()',
  },
})
export class HxCalendarBody<D = Date> {
  // =========================================================================
  // Inputs
  // =========================================================================

  /** ARIA label for the calendar grid. */
  readonly label = input.required<string>();

  /** Rows of calendar cells to display. Each row is an array of cells. */
  readonly rows = input.required<CalendarCell<D>[][]>();

  /** Number of columns in the grid. */
  readonly numCols = input.required<number>();

  /** Index of the currently active (focused) cell. */
  readonly activeCell = input<number>(0);

  /** Index of the selected cell. */
  readonly selectedCell = input<number | null>(null);

  /** Start index of range selection. */
  readonly rangeStart = input<number | null>(null);

  /** End index of range selection. */
  readonly rangeEnd = input<number | null>(null);

  /** Comparison value for determining range preview. */
  readonly previewStart = input<number | null>(null);

  /** Comparison value for preview end. */
  readonly previewEnd = input<number | null>(null);

  /** Aspect ratio for cells (width / height). Default 1 creates square cells. */
  readonly cellAspectRatio = input<number>(1);

  /** Optional weekday labels to display in header row. If provided, a thead with weekday labels will be rendered. */
  readonly weekdayLabels = input<string[] | undefined>(undefined);

  // =========================================================================
  // Outputs
  // =========================================================================

  /** Emits when a cell is selected (clicked or Enter pressed). */
  readonly selectedChange = output<D>();

  /** Emits when a cell is activated (focused). */
  readonly activeDateChange = output<D>();

  /** Emits when mouse enters a cell (for range preview). */
  readonly previewChange = output<D | null>();

  // =========================================================================
  // Computed
  // =========================================================================

  /** Flat array of all cells for easier access by index. */
  protected readonly allCells = computed(() => {
    return this.rows().flat();
  });

  // =========================================================================
  // Cell State Helpers
  // =========================================================================

  /** Check if a cell is selected. */
  protected isSelected(cellIndex: number): boolean {
    return this.selectedCell() === cellIndex;
  }

  /** Check if a cell is active (focused). */
  protected isActive(cellIndex: number): boolean {
    return this.activeCell() === cellIndex;
  }

  /** Check if a cell is in range selection. */
  protected isInRange(cellIndex: number): boolean {
    const start = this.rangeStart();
    const end = this.rangeEnd();

    if (start === null && end === null) {
      return false;
    }

    // Special case: -1 indicates range extends beyond current view
    // If start is -1, range starts from beginning of view
    const effectiveStart = start === -1 ? 0 : start;
    // If end is -1, range extends to end of view
    const effectiveEnd = end === -1 ? Number.MAX_SAFE_INTEGER : end;

    if (effectiveStart === null || effectiveEnd === null) {
      return false;
    }

    const min = Math.min(effectiveStart, effectiveEnd);
    const max = Math.max(effectiveStart, effectiveEnd);

    return cellIndex >= min && cellIndex <= max;
  }

  /** Check if a cell is the start of a range. */
  protected isRangeStart(cellIndex: number): boolean {
    const start = this.rangeStart();
    const end = this.rangeEnd();

    if (start === null || start === -1) {
      return false;
    }

    // Show green for start date even when end is not selected yet
    if (end === null) {
      return cellIndex === start;
    }

    return cellIndex === Math.min(start, end === -1 ? start : end);
  }

  /** Check if a cell is the end of a range. */
  protected isRangeEnd(cellIndex: number): boolean {
    const start = this.rangeStart();
    const end = this.rangeEnd();

    if (start === null || end === null || end === -1) {
      return false;
    }

    return cellIndex === Math.max(start === -1 ? end : start, end);
  }

  /** Check if a cell is in preview range (hover). */
  protected isInPreview(cellIndex: number): boolean {
    const start = this.previewStart();
    const end = this.previewEnd();

    if (start === null || end === null) {
      return false;
    }

    const min = Math.min(start, end);
    const max = Math.max(start, end);

    return cellIndex >= min && cellIndex <= max;
  }

  // =========================================================================
  // Event Handlers
  // =========================================================================

  /** Handle cell click. */
  protected onCellClick(cell: CalendarCell<D>, event: MouseEvent): void {
    if (cell.enabled) {
      event.preventDefault();
      event.stopPropagation();
      this.selectedChange.emit(cell.value);
    }
  }

  /** Handle cell keyboard activation. */
  protected onCellKeydown(cell: CalendarCell<D>, event: KeyboardEvent): void {
    if (cell.enabled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopPropagation();
      this.selectedChange.emit(cell.value);
    }
  }

  /** Handle mouse enter for range preview. */
  protected onCellMouseEnter(cell: CalendarCell<D>): void {
    if (cell.enabled) {
      this.previewChange.emit(cell.value);
    }
  }

  /** Handle mouse leave for range preview. */
  protected onCellMouseLeave(): void {
    this.previewChange.emit(null);
  }

  /** Get CSS classes for a cell. */
  protected getCellClasses(cell: CalendarCell<D>, cellIndex: number): Record<string, boolean> {
    const classes: Record<string, boolean> = {
      'hx-calendar-body__cell--selected': this.isSelected(cellIndex),
      'hx-calendar-body__cell--disabled': !cell.enabled,
      'hx-calendar-body__cell--in-range': this.isInRange(cellIndex),
      'hx-calendar-body__cell--range-start': this.isRangeStart(cellIndex),
      'hx-calendar-body__cell--range-end': this.isRangeEnd(cellIndex),
      'hx-calendar-body__cell--in-preview': this.isInPreview(cellIndex),
    };

    // Add custom CSS classes from cell
    if (cell.cssClasses) {
      cell.cssClasses.forEach((cls) => (classes[cls] = true));
    }

    return classes;
  }

  /** Calculate cell index from row and column indices. */
  protected getCellIndex(rowIndex: number, colIndex: number): number {
    const rows = this.rows();
    let index = 0;

    for (let i = 0; i < rowIndex; i++) {
      index += rows[i]!.length;
    }

    return index + colIndex;
  }
}
